import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import difference from 'lodash/difference';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { Motion } from 'react-motion';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  RESPONSIVE_SIZE,
  FULL_STYLE,
} from '../../constants';
import { loadImage, PRIORITY } from '../../lib/tasks';
import { springify } from '../../lib/Motion';
import { MyPureComponent } from '../../lib/react';
import Aspect from '../Aspect';
import ImageLoader from '../ImageLoader';
import Hoverable from '../Hoverable';
import Style from '../Style';
import { H5, L1, P1, P2 } from '../Typography';

const INSET = 2.5;
const POSTER_DURATION = 1200;
const CLIP_IMAGES_TO_PRELOAD = 3;

class SceneCard extends MyPureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    title: PropTypes.string,
    typeName: PropTypes.string,
    backgroundColor: PropTypes.string,
    lqip: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    highlightColor: PropTypes.string,
    allClips: PropTypes.array,
    matchingClips: PropTypes.array,
    onHoverToggle: PropTypes.func,
    onHoverData: PropTypes.any,
  };
  static defaultProps = {
    title: '',
    typeName: '',
    backgroundColor: Style.Colors.black,
    lqip: undefined,
    images: EMPTY_ARRAY,
    highlightColor: 'transparent',
    allClips: EMPTY_ARRAY,
    matchingClips: undefined,
    onHoverToggle: hovering => {},
    onHoverData: undefined,
  };

  constructor(props, context) {
    super(props, context);

    this.nextClipImageTimeout = undefined;
    this.state = {
      clipImages: this.getClipImages(props),
      clipIndex: 0,
      hover: false,
    };

    this.loadingClipImages = new Set();
    this.loadedClipImages = new Set();

    this.loadClipImages(CLIP_IMAGES_TO_PRELOAD);
  }

  componentWillReceiveProps(nextProps) {
    const clipImages = this.getClipImages(nextProps);

    if (!isEqual(clipImages, this.state.clipImages)) {
      this.setState({ clipImages: clipImages });
      this.loadClipImages(CLIP_IMAGES_TO_PRELOAD);
    }
  }

  getClipImages = props => {
    return (props.matchingClips || props.allClips).map(
      clip => clip.clip_thumbnail,
    );
  };

  componentWillUnmount() {
    clearTimeout(this.nextClipImageTimeout);
  }

  toggleHovering = (hovering, event) => {
    this.setState({ hover: hovering });
    this.nextClipImage();
    this.props.onHoverToggle(hovering, this.props.onHoverData);
  };

  nextClipImage = () => {
    clearTimeout(this.nextClipImageTimeout);
    this.nextClipImageTimeout = setTimeout(() => {
      if (this.state.hover) {
        this.setState({ clipIndex: this.state.clipIndex + 1 });
        this.nextClipImage();
        this.loadClipImages(1, PRIORITY.HIGH);
      }
    }, POSTER_DURATION);
  };

  loadClipImages = (count, priority) => {
    let loaded = 0;
    this.state.clipImages.map(image => {
      if (!image) {
        return;
      }
      if (loaded >= count) {
        return;
      }
      if (this.loadingClipImages.has(image)) {
        return;
      }
      if (this.loadedClipImages.has(image)) {
        return;
      }

      this.loadingClipImages.add(image);

      loadImage(image).then(() => {
        this.loadedClipImages.add(image);
      });

      loaded += 1;
    });
  };

  render() {
    const {
      responsive,
      title,
      typeName,
      backgroundColor,
      lqip,
      images,
      highlightColor,
      allClips,
      matchingClips,
      onHoverToggle,
      onHoverData,
      ...otherProps
    } = this.props;

    const anyClip = get(allClips, 0, EMPTY_OBJECT);

    const matchingClipCount = (matchingClips || EMPTY_ARRAY).length;
    const otherClips = difference(allClips, matchingClips || EMPTY_ARRAY);
    const totalClipCount = anyClip.scene_clip_count || allClips.length;

    let labelString = `view ${typeName}`;
    if (this.state.hover) {
      labelString = `open ${typeName}`;
    }

    let cornerOpacity = 0;
    let SubtextComponent = P2;
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      SubtextComponent = P1;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      cornerOpacity = 1;
    }

    const containerStyle = {
      width: '100%',
      margin: 'auto',
    };
    const backgroundStyle = {
      ...FULL_STYLE,
      backgroundColor: Style.Colors.black,
      color: Style.Colors.white,
      padding: '2.2rem 2rem 0',
      borderRadius: '0.6rem',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
    };
    const imageContainerStyle = {
      ...FULL_STYLE,
      overflow: 'hidden',
      display: 'flex',
    };
    const clipImageStyle = {
      ...FULL_STYLE,
      backgroundSize: 'cover',
    };
    const borderBoxStyle = {
      ...FULL_STYLE,
      margin: `6%`,
      border: `solid 0.2rem ${Style.Colors.white}`,
    };
    const countStyle = {
      position: 'absolute',
      bottom: `${INSET * 1.3}rem`,
      right: `${INSET * 2}rem`,
    };
    const buttonBoxStyle = {
      position: 'relative',
      margin: 'auto',
    };
    const cornerStyleBase = {
      margin: 'auto',
      position: 'absolute',
      width: '15%',
      height: '15%',
      border: `solid 0.4rem ${Style.Colors.white}`,
    };
    const leftCornerStyle = {
      ...cornerStyleBase,
      borderRight: 'none',
      borderTop: 'none',
    };
    const rightCornerStyle = {
      ...cornerStyleBase,
      borderLeft: 'none',
      borderBottom: 'none',
    };
    const infoBoxStyle = {
      // flexGrow: 1,
      // display: 'flex',
      // flexDirection: 'column',
      // justifyContent: 'center',
      padding: `0.5rem ${INSET}rem`,
    };

    const animatedDefault = {
      matchingCount: 0,
      cornerPositionH: 32,
      cornerPositionV: 40,
      hover: 0,
      lqip: 1,
      clipImagesMult: 0,
    };
    const animatedCurrent = {
      ...springify({
        matchingCount: matchingClipCount,
        hover: this.state.hover ? 1 : animatedDefault.hover,
        lqip: this.state.scenePosterLoaded ? 0 : 1,
        clipImagesMult: this.loadedClipImages.size > 0 ? 1 : 0,
      }),
      ...springify(
        {
          cornerPositionH: this.state.hover
            ? 9
            : animatedDefault.cornerPositionH,
          cornerPositionV: this.state.hover
            ? 16
            : animatedDefault.cornerPositionV,
        },
        {
          stiffness: this.state.hover ? 400 : 170,
          damping: this.state.hover ? 20 : 26,
        },
      ),
    };

    return (
      <Motion defaultStyle={animatedDefault} style={animatedCurrent}>
        {tween => {
          // Animated properties.
          let countString = '';
          let infoClipString = '';
          if (matchingClips === undefined) {
            countString = null;
            infoClipString = `${totalClipCount} Clips`;
          } else if (totalClipCount) {
            countString = `${Math.round(
              tween.matchingCount,
            )} of ${totalClipCount}`;
            infoClipString = `${Math.round(
              tween.matchingCount,
            )} Matching Clips`;
          }

          const backgroundStyleMerged = {
            ...backgroundStyle,
            boxShadow: `${highlightColor} ${tween.hover *
              1.4}rem ${tween.hover * -1}rem 0 0`,
          };
          const borderBoxStyleMerged = {
            ...borderBoxStyle,
            opacity: tween.hover * 0.7 + 0.3,
          };
          const countStyleMerged = {
            ...countStyle,
            opacity: tween.hover * 0.5 + 0.5,
          };
          let cornerValueH = `${tween.cornerPositionH}%`;
          let cornerValueV = `${tween.cornerPositionV}%`;
          const leftCornerStyleMerged = {
            ...leftCornerStyle,
            bottom: cornerValueV,
            left: cornerValueH,
            opacity: cornerOpacity,
          };
          const rightCornerStyleMerged = {
            ...rightCornerStyle,
            top: cornerValueV,
            right: cornerValueH,
            opacity: cornerOpacity,
          };

          const thisClipImage = Array.from(this.loadedClipImages)[
            this.state.clipIndex % this.loadedClipImages.size
          ];
          // Just "fetching" the image isn't enough. There is a flash as they are loaded
          //  into memory or something. Putting them in the dom early seems to solve this.
          const nextClipImage = Array.from(this.loadedClipImages)[
            (this.state.clipIndex + 1) % this.loadedClipImages.size
          ];

          return (
            <div style={containerStyle}>
              <Hoverable onChange={this.toggleHovering}>
                <Aspect ratio={3 / 4}>
                  <div style={backgroundStyleMerged}>
                    <Aspect ratio={9 / 16}>
                      <div style={imageContainerStyle}>
                        <ImageLoader
                          color={backgroundColor}
                          lqip={lqip}
                          images={images}
                        />
                        <div
                          style={{
                            ...clipImageStyle,
                            backgroundImage: `url(${nextClipImage})`,
                            opacity: 0,
                          }}
                        />
                        <div
                          style={{
                            ...clipImageStyle,
                            backgroundColor: Style.Colors.black,
                            backgroundImage: `url(${thisClipImage})`,
                            opacity: tween.hover * tween.clipImagesMult,
                          }}
                        />
                        <div style={borderBoxStyleMerged} />
                        <H5 style={countStyleMerged}>{countString}</H5>
                        <div style={leftCornerStyleMerged} />
                        <div style={rightCornerStyleMerged} />
                        <div style={buttonBoxStyle}>
                          <L1>{labelString.toUpperCase()}</L1>
                        </div>
                      </div>
                    </Aspect>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1,
                      }}
                    >
                      <div style={infoBoxStyle}>
                        <H5
                          style={{
                            marginBottom: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {title}
                        </H5>
                        <SubtextComponent
                          style={{
                            marginTop: 0,
                            color: Style.Colors.shadow4,
                          }}
                        >
                          {typeName}&nbsp;&nbsp;•&nbsp;&nbsp;{infoClipString}
                        </SubtextComponent>
                      </div>
                    </div>
                  </div>
                </Aspect>
              </Hoverable>
            </div>
          );
        }}
      </Motion>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
  };
}

export default connect(mapStateToProps)(SceneCard);



// WEBPACK FOOTER //
// ./src/components/SceneCard/SceneCard.js